import type { SessionEvent, ContextUpdateData } from '../types/sessions.js';
import type { ParticipantStatus, RunningBackgroundTask } from '../types/sessions.js';
import type { RagHitRef } from './types.js';
import type {
  ProcessedState,
  ChatMessage,
  ChatState,
  ProgressTree,
  ProgressNode,
  ProgressStep,
  LlmCallPurpose,
  LoopExitReason,
  ChatQuestion,
  QuestionOption,
  PlatformApprovalRequest,
  MediaActionApprovalRequest,
  WakeSource,
  ChatParticipant,
  SelectedProcess,
  SelectedProcessIntent,
} from './types.js';
import { normalizeInternalAgentProvenance } from './internal-agent-provenance.js';

interface InternalRun {
  runIndex: number;
  runId: string;
  runOrdinal: number | null;
  startedAt: string;
  endedAt: string | null;
  status: 'in_progress' | 'completed' | 'failed' | 'stopped';
  error?: string;
  workspaceRefusal?: string;
  steps: ProgressStep[];
  nodes: ProgressNode[];
  contextUsagePercent: number;
  workflowId?: string;
  assistantMessageId?: string;
  agentConfig?: Record<string, unknown>;
  toolNames?: string[];
  selectedProcess?: SelectedProcess;
  selectingProcessSince?: string;
  internalAgents: NonNullable<ProgressTree['internalAgents']>;
  trigger?: ProgressTree['trigger'];
  wakeSources?: ProgressTree['wakeSources'];
}

function captureInternalAgent(run: InternalRun, e: SessionEvent): void {
  const provenance = normalizeInternalAgentProvenance({
    personaName: e.data.internal_agent_persona_name as string | undefined,
    processId: e.data.internal_agent_process_id as string | undefined,
    processName: e.data.internal_agent_process_name as string | undefined,
  });
  if (!provenance) return;
  const key = `${provenance.personaName}:${provenance.processId ?? ''}`;
  if (!run.internalAgents.some((item) => `${item.personaName}:${item.processId ?? ''}` === key)) {
    run.internalAgents.push(provenance);
  }
}

// (sessions ADR-1e992)
function applyTurnTrigger(run: InternalRun, e: SessionEvent): void {
  const trigger = e.data.trigger;
  if (trigger !== 'user' && trigger !== 'agent_message' && trigger !== 'background') return;
  run.trigger = trigger;
  if (trigger === 'user' || !Array.isArray(e.data.sources)) return;
  run.wakeSources = (e.data.sources as Array<Record<string, unknown>>).map((s): WakeSource => {
    if (typeof s.id !== 'string' || (s.kind !== 'participant' && s.kind !== 'tool' && s.kind !== 'plan')) {
      throw new Error(`reasoning_started at sequence ${e.sequence_num} carries a malformed wake source`);
    }
    if (typeof s.label !== 'string' || !s.label) {
      throw new Error(`reasoning_started at sequence ${e.sequence_num} carries wake source ${s.id} with no label`);
    }
    return { kind: s.kind, id: s.id, label: s.label, ...(typeof s.text === 'string' && s.text ? { text: s.text } : {}) };
  });
}

function findNode(run: InternalRun, id: string): ProgressNode | undefined {
  return run.nodes.find(n => n.id === id);
}

function requireString(e: SessionEvent, key: string): string {
  const v = e.data[key];
  if (typeof v !== 'string') throw new Error(`${e.event_type} event ${e.sequence_num} carries no ${key}`);
  return v;
}

function optionalString(e: SessionEvent, key: string): string | null {
  const v = e.data[key];
  if (v === undefined || v === null || v === '') return null;
  if (typeof v !== 'string') throw new Error(`${e.event_type} event ${e.sequence_num} carries a non-string ${key}`);
  return v;
}

// (sessions ADR-36b60)
function readQuestion(e: SessionEvent): ChatQuestion {
  const status = requireString(e, 'status');
  if (status !== 'open' && status !== 'answered' && status !== 'declined' && status !== 'cancelled') {
    throw new Error(`${e.event_type} event ${e.sequence_num} carries an unknown status ${status}`);
  }
  const addresseeKind = requireString(e, 'addressee_kind');
  if (addresseeKind !== 'user' && addresseeKind !== 'participant') {
    throw new Error(`${e.event_type} event ${e.sequence_num} carries an unknown addressee_kind ${addresseeKind}`);
  }
  const answeredByKind = optionalString(e, 'answered_by_kind');
  if (answeredByKind !== null && answeredByKind !== 'user' && answeredByKind !== 'participant') {
    throw new Error(`${e.event_type} event ${e.sequence_num} carries an unknown answered_by_kind ${answeredByKind}`);
  }
  const options = e.data.options;
  const indexes = e.data.answer_option_indexes;
  if (!Array.isArray(options) || !Array.isArray(indexes)) {
    throw new Error(`${e.event_type} event ${e.sequence_num} carries no options or answer_option_indexes`);
  }
  return {
    questionId: requireString(e, 'question_id'),
    askerParticipantId: requireString(e, 'asker_participant_id'),
    askerName: requireString(e, 'asker_name'),
    askerIsPrimary: e.data.asker_is_primary === true,
    askerSessionId: requireString(e, 'asker_session_id'),
    addresseeKind,
    addresseeSessionId: requireString(e, 'addressee_session_id'),
    addresseeParticipantId: optionalString(e, 'addressee_participant_id'),
    escalatedFromParticipantId: optionalString(e, 'escalated_from_participant_id'),
    question: requireString(e, 'question'),
    options: (options as QuestionOption[]).map((o) => ({ label: o.label, ...(o.description ? { description: o.description } : {}) })),
    multiSelect: e.data.multi_select === true,
    default: requireString(e, 'default'),
    status,
    answerOptionIndexes: indexes as number[],
    answerText: optionalString(e, 'answer_text'),
    answeredByKind,
    answeredById: optionalString(e, 'answered_by_id'),
    cancelReason: optionalString(e, 'cancel_reason'),
    askedAt: requireString(e, 'asked_at'),
    resolvedAt: optionalString(e, 'resolved_at'),
  };
}

// (sessions ADR-36b60)
function foldQuestion(questions: Map<string, ChatQuestion>, e: SessionEvent): void {
  const next = readQuestion(e);
  const prior = questions.get(next.questionId);
  if (prior && prior.status !== 'open' && next.status === 'open') return;
  questions.set(next.questionId, next);
}

function participantOf(e: SessionEvent): string | undefined {
  const id = e.data.participant_id;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

function isParticipantNode(node: ProgressNode): boolean {
  return node.participantId !== undefined && node.participantId === node.id;
}

function participantStatusOf(status: ProgressNode['status']): ParticipantStatus {
  switch (status) {
    case 'running':
      return 'running';
    case 'completed':
      return 'settled';
    case 'failed':
      return 'failed';
    case 'stopped':
      return 'stopped';
  }
}

function nodeStatusOf(status: ParticipantStatus): ProgressNode['status'] {
  switch (status) {
    case 'running':
    case 'idle':
      return 'running';
    case 'settled':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'stopped':
      return 'stopped';
  }
}

// (sessions ADR-34ae2)
function applyParticipantStatus(node: ProgressNode, status: ParticipantStatus, at: string): void {
  node.participantStatus = status;
  node.status = nodeStatusOf(status);
  if (node.status === 'running') node.endedAt = undefined;
  else node.endedAt ??= at;
}

function readMediaActionApproval(e: SessionEvent): MediaActionApprovalRequest | null {
  const approvalId = e.data.approval_id;
  const toolName = e.data.tool_name;
  const args = e.data.arguments;
  if (typeof approvalId !== 'string' || typeof toolName !== 'string' || !args || typeof args !== 'object') return null;
  const workflowId = e.data.workflow_id;
  return {
    approvalId,
    toolName,
    arguments: args as Record<string, unknown>,
    ...(typeof workflowId === 'string' && workflowId.length > 0 ? { workflowId } : {}),
  };
}

function snapshotNode(p: ChatParticipant): ProgressNode {
  return {
    id: p.id,
    ...(p.spawnTaskId !== null ? { parentId: p.spawnTaskId } : {}),
    label: p.label,
    status: nodeStatusOf(p.status),
    steps: [],
    children: [],
    startedAt: '',
    ...(p.personaName !== null ? { personaName: p.personaName } : {}),
    artifacts: [],
    decisions: [],
    tokenCount: 0,
    participantId: p.id,
    background: p.background,
    ...(p.agentId !== null ? { agentId: p.agentId } : {}),
    participantStatus: p.status,
  };
}

function handleTaskStarted(run: InternalRun, e: SessionEvent): ProgressNode | undefined {
  const id = e.data.id as string;
  if (run.nodes.some(n => n.id === id)) return undefined;
  const participantId = participantOf(e);
  const ownNode = participantId !== undefined && participantId === id;
  const personaName = (e.data.persona_name as string) ?? '';
  const stepId = typeof e.data.step_id === 'string' ? e.data.step_id : undefined;
  const processId = e.data.process_id as string | undefined;
  const processName = e.data.process_name as string | undefined;
  run.nodes.push({
    id,
    parentId: e.data.parent_id as string | undefined,
    label: (e.data.label ?? personaName) as string,
    status: 'running',
    steps: [],
    children: [],
    startedAt: e.created_at,
    cognitiveToolType: e.data.cognitive_tool_type as string | undefined,
    stepId,
    personaName,
    processId,
    processName,
    internalAgent: normalizeInternalAgentProvenance({ personaName, processId, processName }),
    fanoutId: typeof e.data.fanout_id === 'string' ? e.data.fanout_id : undefined,
    fanoutSize: typeof e.data.fanout_size === 'number' ? e.data.fanout_size : undefined,
    promptPreview: e.data.prompt_preview as string | undefined,
    artifacts: [],
    decisions: [],
    tokenCount: 0,
    participantId,
    ...(!ownNode && e.data.background === true ? { background: true, backgroundKind: e.data.background_kind === 'plan' ? 'plan' as const : 'tool' as const } : {}),
    ...(ownNode
      ? {
          background: e.data.background === true,
          backgroundKind: 'participant' as const,
          agentId: typeof e.data.agent_id === 'string' && e.data.agent_id ? e.data.agent_id : undefined,
          participantStatus: 'running' as const,
        }
      : {}),
  });
  return run.nodes[run.nodes.length - 1];
}

function handleTaskCompleted(run: InternalRun, e: SessionEvent): void {
  const id = e.data.id as string;
  const node = findNode(run, id);
  if (node) {
    node.status = (e.data.status as 'completed' | 'failed' | 'stopped') ?? 'completed';
    if (isParticipantNode(node)) node.participantStatus = participantStatusOf(node.status);
    node.conclusionSummary = e.data.conclusion_summary as string | undefined;
    node.durationMs = e.data.duration_ms as number | undefined;
    node.error = e.data.error as string | undefined;
    node.endedAt = e.created_at;
    node.artifacts = (e.data.artifacts as string[]) ?? node.artifacts;
    node.decisions = (e.data.decisions as string[]) ?? node.decisions;
    node.tokenCount = (e.data.token_count as number) ?? node.tokenCount;
    if (e.data.cognitive_tool_type) {
      node.cognitiveToolType = e.data.cognitive_tool_type as string;
    }
    if (typeof e.data.exit_reason === 'string') {
      node.exitReason = e.data.exit_reason as LoopExitReason;
    }
    if (Array.isArray(e.data.failed_tools)) {
      node.failedTools = e.data.failed_tools as string[];
    }
  }
}

function findStepByCallId(run: InternalRun, callId: string): ProgressStep | undefined {
  const inRoot = run.steps.find((s) => s.callId === callId);
  if (inRoot) return inRoot;
  for (const node of run.nodes) {
    const found = node.steps.find((s) => s.callId === callId);
    if (found) return found;
  }
  return undefined;
}

function llmCompletionFields(e: SessionEvent): Partial<ProgressStep> {
  return {
    hasToolCalls: (e.data.has_tool_calls as boolean) ?? false,
    durationMs: e.data.duration_ms as number | undefined,
    tokens: e.data.tokens as number | undefined,
    promptTokens: e.data.prompt_tokens as number | undefined,
    completionTokens: e.data.completion_tokens as number | undefined,
    tokensPerSecond: e.data.tokens_per_second as number | undefined,
    modelName: e.data.model_name as string | undefined,
    providerType: e.data.provider_type as string | undefined,
    thinkingPreview: e.data.thinking_preview as string | undefined,
    responsePreview: e.data.response_preview as string | undefined,
    toolNames: e.data.tool_names as string[] | undefined,
    error: e.data.error as string | undefined,
  };
}

function completeLlmStep(step: ProgressStep, fields: Partial<ProgressStep>): void {
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined) (step as unknown as Record<string, unknown>)[key] = value;
  }
  step.status = fields.error ? 'failed' : 'completed';
}

function closeLlmStepByCallId(run: InternalRun, e: SessionEvent, fields: Partial<ProgressStep>): boolean {
  const callId = typeof e.data.call_id === 'string' ? e.data.call_id : undefined;
  if (!callId) return false;
  const step = findStepByCallId(run, callId);
  if (!step) return false;
  completeLlmStep(step, fields);
  if (!step.durationMs && step.startedAt) {
    const elapsed = new Date(e.created_at).getTime() - new Date(step.startedAt).getTime();
    if (elapsed > 0) step.durationMs = elapsed;
  }
  return true;
}

function handleTaskFailed(run: InternalRun, e: SessionEvent): void {
  const id = e.data.id as string;
  const node = findNode(run, id);
  if (node) {
    node.status = 'failed';
    node.error = (e.data.error as string) ?? 'Agent failed';
    node.endedAt = e.created_at;
    if (isParticipantNode(node)) node.participantStatus = 'failed';
  }
}

function stopSubtree(run: InternalRun, rootId: string, at: string): string[] {
  const stopped = new Set([rootId]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const node of run.nodes) {
      if (!stopped.has(node.id) && node.parentId !== undefined && stopped.has(node.parentId)) {
        stopped.add(node.id);
        grew = true;
      }
    }
  }
  const participants: string[] = [];
  for (const node of run.nodes) {
    if (!stopped.has(node.id) || node.status !== 'running') continue;
    node.status = 'stopped';
    node.endedAt = at;
    if (isParticipantNode(node)) {
      node.participantStatus = 'stopped';
      participants.push(node.id);
    }
  }
  return participants;
}

function appendStepToRun(run: InternalRun, e: SessionEvent): void {
  const nodeId = e.data.id as string | undefined;

  const targetSteps = nodeId ? findNode(run, nodeId)?.steps : undefined;
  const steps = targetSteps ?? run.steps;
  const firstNew = steps.length;
  appendStep(run, e, steps, nodeId);
  const participantId = participantOf(e);
  if (participantId === undefined) return;
  for (let i = firstNew; i < steps.length; i++) steps[i].participantId = participantId;
}

function appendStep(run: InternalRun, e: SessionEvent, steps: ProgressStep[], nodeId: string | undefined): void {
  switch (e.event_type) {
    case 'triage':
      steps.push({
        type: 'triage',
        timestamp: e.created_at,
        nodeId,
        iteration: 0,
        guidancePreview: e.data.guidance_preview as string | undefined,
      });
      break;

    case 'llm_call_start': {
      const callId = typeof e.data.call_id === 'string' ? e.data.call_id : undefined;
      const existing = callId ? findStepByCallId(run, callId) : undefined;
      if (existing) {
        existing.startedAt = e.created_at;
        break;
      }
      steps.push({
        type: 'llm_call',
        timestamp: e.created_at,
        startedAt: e.created_at,
        status: 'running',
        callId,
        purpose: (e.data.purpose as LlmCallPurpose | undefined) ?? 'loop',
        nodeId,
        iteration: (e.data.iteration as number) ?? 0,
        modelName: e.data.model_name as string | undefined,
        providerType: e.data.provider_type as string | undefined,
        promptTokensEstimate: e.data.prompt_tokens_estimate as number | undefined,
        internalAgent: normalizeInternalAgentProvenance({
          personaName: e.data.internal_agent_persona_name as string | undefined,
          processId: e.data.internal_agent_process_id as string | undefined,
          processName: e.data.internal_agent_process_name as string | undefined,
        }),
      });
      break;
    }

    case 'llm_call': {
      const fields = llmCompletionFields(e);
      if (closeLlmStepByCallId(run, e, fields)) break;
      const step: ProgressStep = {
        type: 'llm_call',
        timestamp: e.created_at,
        callId: typeof e.data.call_id === 'string' ? e.data.call_id : undefined,
        purpose: (e.data.purpose as LlmCallPurpose | undefined) ?? 'loop',
        nodeId,
        iteration: (e.data.iteration as number) ?? 0,
      };
      completeLlmStep(step, fields);
      steps.push(step);
      break;
    }

    case 'process_evaluation':
    case 'process_derivation':
      closeLlmStepByCallId(run, e, {
        modelName: e.data.model_name as string | undefined,
        providerType: e.data.provider_type as string | undefined,
      });
      break;

    case 'tool_call_start':
      steps.push({
        type: 'tool_call',
        timestamp: e.created_at,
        toolName: e.data.tool_name as string | undefined,
        toolArgsPreview: e.data.args_preview as string | undefined,
        nodeId,
        childIdAtStart: e.data.child_id as string | undefined,
        iteration: (e.data.iteration as number) ?? 0,
      });
      break;

    case 'tool_call_result': {
      const toolName = e.data.tool_name as string | undefined;
      for (let j = steps.length - 1; j >= 0; j--) {
        if (steps[j].type === 'tool_call' && steps[j].toolName === toolName && !steps[j].toolResultStatus) {
          steps[j].toolResultStatus = e.data.status as string;
          steps[j].toolResultError = e.data.error as string | undefined;
          steps[j].toolResultPreview =
            (e.data.result_preview as string | undefined) || (e.data.error as string | undefined);
          if (e.data.child_id) {
            steps[j].childId = e.data.child_id as string;
          }
          const startTime = new Date(steps[j].timestamp).getTime();
          const endTime = new Date(e.created_at).getTime();
          if (startTime > 0 && endTime > startTime) {
            steps[j].durationMs = endTime - startTime;
          }
          break;
        }
      }
      break;
    }

    case 'context_rag_started':
      // Run-start retrieval step (started has no payload beyond identity).
      if (steps.some((s) => s.type === 'context_rag' && s.nodeId === nodeId)) break;
      steps.push({
        type: 'context_rag',
        timestamp: e.created_at,
        nodeId,
        startedAt: e.created_at,
        status: 'running',
      });
      break;

    case 'context_rag_completed': {
      // Complete the step the started event opened; if the started frame never
      // arrived (pre-connection backlog boundary), the completion stands alone
      // as a terminal step rather than vanishing.
      const d = e.data as {
        outcome?: 'hits' | 'empty' | 'failed';
        hit_count?: number;
        hit_refs?: RagHitRef[];
        indexed_entries?: number;
      };
      const outcome = d.outcome ?? 'failed';
      const fields = {
        ragOutcome: outcome,
        hitCount: d.hit_count ?? 0,
        hitRefs: d.hit_refs ?? [],
        indexedEntries: d.indexed_entries,
      };
      const pending = [...steps].reverse().find((s) => s.type === 'context_rag' && s.nodeId === nodeId);
      if (pending) {
        pending.status = outcome === 'failed' ? 'failed' : 'completed';
        Object.assign(pending, fields);
      } else {
        steps.push({
          type: 'context_rag',
          timestamp: e.created_at,
          nodeId,
          status: outcome === 'failed' ? 'failed' : 'completed',
          ...fields,
        });
      }
      break;
    }

    case 'context_update': {
      // Typed payload. The
      // headline fields are OPTIONAL — omitted when the provider returned no
      // usage — so usagePercent stays undefined (the step renders no percent,
      // never a fabricated 0%). supersededEntries feeds the dedupe subtitle.
      const d = e.data as unknown as ContextUpdateData;
      steps.push({
        type: 'context_update',
        timestamp: e.created_at,
        nodeId,
        usagePercent: d.usage_percent,
        overflowHandled: d.overflow_handled ?? false,
        iteration: d.iteration ?? 0,
        totalContextTokens: d.total_tokens,
        contextWindowSize: d.context_window_size,
        supersededEntries: d.superseded_entries ?? [],
        promptTokens: d.prompt_tokens,
        completionTokens: d.completion_tokens,
        attribution: d.attribution,
        inContextEntries: d.in_context_entries,
        compactedEntries: d.compacted_entries,
        messageCount: d.message_count,
        personaName: d.persona_name,
        staleMessageCount: d.stale_message_count,
        tokensSavedByDedup: d.tokens_saved_by_dedup,
        messagesSummarized: d.messages_summarized,
        tokensReduced: d.tokens_reduced,
        summarizerProviderType: d.summarizer_provider_type,
        summarizerModelName: d.summarizer_model_name,
      });
      break;
    }
  }
}

function buildNodeTree(nodes: ProgressNode[]): void {
  const byId = new Map<string, ProgressNode>();
  for (const node of nodes) {
    byId.set(node.id, node);
    node.children = [];
  }

  for (const node of nodes) {
    if (node.parentId) {
      const parent = byId.get(node.parentId);
      if (parent) {
        parent.children.push(node);
      }
    }
  }
}

function rerouteOrphanedSteps(run: InternalRun): void {
  if (run.steps.length === 0) return;
  const nodeMap = new Map(run.nodes.map(n => [n.id, n]));
  if (nodeMap.size === 0) return;

  const movedToNodes = new Set<string>();
  for (let i = run.steps.length - 1; i >= 0; i--) {
    const step = run.steps[i];
    if (step.nodeId && nodeMap.has(step.nodeId)) {
      const node = nodeMap.get(step.nodeId)!;
      node.steps.push(step);
      run.steps.splice(i, 1);
      movedToNodes.add(step.nodeId);
    }
  }

  for (const id of movedToNodes) {
    const node = nodeMap.get(id)!;
    node.steps.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  }
}

function requireRunId(e: SessionEvent): string {
  const runId = e.data.run_id;
  if (typeof runId !== 'string' || !runId) {
    throw new Error(`${e.event_type} at sequence ${e.sequence_num} has no run_id`);
  }
  return runId;
}

// (sdk ADR-78ebf)
function requireRunOrdinal(e: SessionEvent): number {
  const ordinal = e.data.run_ordinal;
  if (typeof ordinal !== 'number' || !Number.isInteger(ordinal) || ordinal < 1) {
    throw new Error(`reasoning_started at sequence ${e.sequence_num} has no run_ordinal`);
  }
  return ordinal;
}

function runToProgressTree(run: InternalRun): ProgressTree {
  return {
    runIndex: run.runIndex,
    runId: run.runId,
    runOrdinal: run.runOrdinal,
    status: run.status,
    startedAt: run.startedAt,
    endedAt: run.endedAt,
    error: run.error,
    workspaceRefusal: run.workspaceRefusal,
    steps: run.steps,
    nodes: run.nodes,
    contextUsagePercent: run.contextUsagePercent,
    assistantMessageId: run.assistantMessageId,
    agentConfig: run.agentConfig,
    toolNames: run.toolNames,
    selectedProcess: run.selectedProcess,
    selectingProcessSince: run.status === 'in_progress' ? run.selectingProcessSince : undefined,
    internalAgents: run.internalAgents,
    ...(run.trigger ? { trigger: run.trigger } : {}),
    ...(run.wakeSources ? { wakeSources: run.wakeSources } : {}),
  };
}

type PlatformApprovalSignal =
  | { kind: 'pending'; request: PlatformApprovalRequest }
  | { kind: 'cleared' }
  | { kind: 'none' };

interface PlatformCallStart {
  resource: string;
  method: string;
  arguments: Record<string, unknown>;
}

function readPlatformCallStart(e: SessionEvent): PlatformCallStart | null {
  const where = `platform_call tool_call_start at sequence ${e.sequence_num}`;
  if (!('args_preview' in e.data)) {
    if (e.handle !== undefined) return null;
    throw new Error(`${where} has no args_preview and no payload handle`);
  }
  const raw = e.data.args_preview;
  if (typeof raw !== 'string') {
    throw new Error(`${where} has a non-string args_preview`);
  }
  if (raw === '') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new Error(`${where} has an args_preview that is not JSON: ${(err as Error).message}`);
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const call = parsed as Record<string, unknown>;
  const args = call.arguments;
  if (typeof call.resource !== 'string' || typeof call.method !== 'string') return null;
  if (typeof args !== 'object' || args === null || Array.isArray(args)) return null;
  return { resource: call.resource, method: call.method, arguments: args as Record<string, unknown> };
}

// (agents ADR-14bdc)
function readWaitedQuestionIds(e: SessionEvent): string[] | null {
  const where = `wait_for_answers tool_call_start at sequence ${e.sequence_num}`;
  if (!('args_preview' in e.data)) {
    if (e.handle !== undefined) return null;
    throw new Error(`${where} has no args_preview and no payload handle`);
  }
  const raw = e.data.args_preview;
  if (typeof raw !== 'string') {
    throw new Error(`${where} has a non-string args_preview`);
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new Error(`${where} has an args_preview that is not JSON: ${(err as Error).message}`);
  }
  const ids = (parsed as { question_ids?: unknown } | null)?.question_ids;
  if (!Array.isArray(ids) || !ids.every((id): id is string => typeof id === 'string')) {
    throw new Error(`${where} names no question_ids`);
  }
  return ids;
}

function readPlatformApproval(
  e: SessionEvent,
  lastStart: PlatformCallStart | null,
): PlatformApprovalSignal {
  const preview = e.data.result_preview;
  if (typeof preview !== 'string') return { kind: 'none' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(preview);
  } catch {
    return { kind: 'none' };
  }
  if (!parsed || typeof parsed !== 'object') return { kind: 'none' };

  const obj = parsed as Record<string, unknown>;
  if (obj.status === 'confirmation_required') {
    const key = obj.idempotency_key;
    const p = obj.preview;
    if (typeof key === 'string' && p && typeof p === 'object') {
      const pv = p as Record<string, unknown>;
      if (typeof pv.resource === 'string' && typeof pv.method === 'string') {
        return {
          kind: 'pending',
          request: {
            resource: pv.resource,
            method: pv.method,
            idempotencyKey: key,
            arguments:
              lastStart && lastStart.resource === pv.resource && lastStart.method === pv.method
                ? lastStart.arguments
                : null,
          },
        };
      }
    }
    return { kind: 'none' };
  }

  if (e.data.tool_name === 'platform_call') {
    return { kind: 'cleared' };
  }
  return { kind: 'none' };
}

const SELECTED_PROCESS_INTENTS: readonly SelectedProcessIntent[] = ['conversation', 'artifact', 'other'];

function selectedProcessFrom(data: Record<string, unknown>): SelectedProcess | undefined {
  const { selected_process_id: id, selected_process_name: name, selected_process_reason: reason } = data;
  if (typeof id !== 'string' || typeof name !== 'string' || typeof reason !== 'string') return undefined;
  const intent = SELECTED_PROCESS_INTENTS.find(value => value === data.selected_user_intent);
  if (!intent) {
    throw new Error(`process selection for ${id} carries no valid selected_user_intent: ${JSON.stringify(data.selected_user_intent)}`);
  }
  const selection: SelectedProcess = { id, name, reason, intent };
  if (typeof data.selected_intent_detail === 'string') selection.intentDetail = data.selected_intent_detail;
  return selection;
}

/**
 * Resolve the run an event belongs to. Prefers the event's own `run_id`; falls
 * back to the open run only for histories recorded before the key existed.
 */
function runForEvent(
  runsById: Map<string, InternalRun>,
  currentRun: InternalRun | null,
  e: SessionEvent,
): InternalRun | null {
  const key = typeof e.data.run_id === 'string' ? e.data.run_id : '';
  if (key) {
    const keyed = runsById.get(key);
    if (keyed) return keyed;
  }
  return currentRun;
}

export interface ProcessEventsOptions {
  /** The session's primary participant, from the session snapshot. */
  primaryParticipantId?: string | null;
  /** The session snapshot's subagent participants; a live one keeps a node when its start is not loaded. */
  participants?: readonly ChatParticipant[];
  /** Starts of background work released from the loaded window while it was live (sdk ADR-f43c7). */
  resident?: readonly SessionEvent[];
  /** The session snapshot's running background tasks; one counts until its loaded node shows it ended. */
  backgroundTasks?: readonly RunningBackgroundTask[];
  /** Open questions to the user read from the list route, including ones asked below the loaded window (sessions ADR-c53f5). */
  seedQuestions?: readonly ChatQuestion[];
}

/**
 * Fold a session's events into chat state. Runs are the primary participant's
 * turns only: a subagent participant's events never open, close or stop a run;
 * its lifecycle node nests under the spawn node in the owner's run and its
 * steps attach to that node. Live work whose start is below the loaded window
 * (a snapshot participant, or a `resident` background start) keeps a node, carried
 * into the newest loaded run (sdk ADR-f43c7).
 */
export function processEvents(rawEvents: SessionEvent[], options: ProcessEventsOptions = {}): ProcessedState {
  const seen = new Set<number>();
  const events: SessionEvent[] = [];
  for (const e of rawEvents) {
    if (!seen.has(e.sequence_num)) {
      seen.add(e.sequence_num);
      events.push(e);
    }
  }
  events.sort((a, b) => a.sequence_num - b.sequence_num);

  let primaryParticipantId: string | null = options.primaryParticipantId ?? null;
  const subagentIds = new Set<string>();
  for (const e of events) {
    const pid = participantOf(e);
    if (pid === undefined) continue;
    if (primaryParticipantId === null && e.event_type === 'reasoning_started') primaryParticipantId = pid;
    if (e.event_type === 'task_started' && e.data.id === pid) subagentIds.add(pid);
  }
  for (const p of options.participants ?? []) {
    if (!p.isPrimary && p.id !== primaryParticipantId) subagentIds.add(p.id);
  }
  const isSubagentEvent = (e: SessionEvent): boolean => {
    const pid = participantOf(e);
    if (pid === undefined) return false;
    return subagentIds.has(pid) || (primaryParticipantId !== null && pid !== primaryParticipantId);
  };
  const nodeRun = new Map<string, InternalRun>();
  const participantStatuses = new Map<string, ParticipantStatus>();
  const participantMediaApprovals = new Map<string, MediaActionApprovalRequest>();
  const detached: InternalRun = {
    runIndex: -1,
    runId: '',
    runOrdinal: null,
    startedAt: '',
    endedAt: null,
    status: 'in_progress',
    steps: [],
    nodes: [],
    internalAgents: [],
    contextUsagePercent: 0,
  };
  for (const p of options.participants ?? []) {
    if (p.isPrimary || p.id === primaryParticipantId) continue;
    if (p.status !== 'running' && p.status !== 'idle') continue;
    detached.nodes.push(snapshotNode(p));
    nodeRun.set(p.id, detached);
    participantStatuses.set(p.id, p.status);
  }
  for (const e of options.resident ?? []) {
    const node = handleTaskStarted(detached, e);
    if (node) nodeRun.set(node.id, detached);
  }

  const messages: ChatMessage[] = [];
  const runs: InternalRun[] = [];
  // Runs are addressed by `run_id`, not by a moving pointer. With two concurrent
  // roots the pointer model could never close the first one — every terminal
  // handler mutated whatever run happened to be current — so it rendered
  // in_progress forever, and re-folding a persisted history reproduced it
  // exactly (agents ADR-043).
  const runsById = new Map<string, InternalRun>();
  let currentRun: InternalRun | null = null;
  const questions = new Map<string, ChatQuestion>((options.seedQuestions ?? []).map((q) => [q.questionId, q]));
  let primaryWaitedIds: string[] | null = null;
  let pendingPlatformApproval: PlatformApprovalRequest | null = null;
  let lastPlatformCallStart: PlatformCallStart | null = null;
  const subagentPlatformStarts = new Map<string, PlatformCallStart | null>();
  let pendingMediaActionApproval: MediaActionApprovalRequest | null = null;
  let workflowId: string | null = null;
  let lastStopReason: string | null = null;

  function foldSubagentEvent(e: SessionEvent): void {
    const pid = participantOf(e) as string;
    const nodeId = typeof e.data.id === 'string' ? e.data.id : undefined;
    switch (e.event_type) {
      case 'task_started':
      case 'step_started': {
        if (nodeId === undefined) return;
        const known = nodeRun.get(nodeId);
        if (known !== undefined && known !== detached) return;
        const parentId = typeof e.data.parent_id === 'string' ? e.data.parent_id : undefined;
        const loaded = parentId !== undefined ? nodeRun.get(parentId) : currentRun;
        if (known === detached && (loaded === undefined || loaded === detached)) return;
        const target = loaded ?? (nodeId === pid ? detached : undefined);
        if (!target) return;
        if (known === detached) detached.nodes = detached.nodes.filter((n) => n.id !== nodeId);
        const node = handleTaskStarted(target, e);
        if (node) nodeRun.set(node.id, target);
        if (nodeId === pid) participantStatuses.set(pid, 'running');
        return;
      }
      case 'task_completed':
      case 'step_completed': {
        const target = nodeId !== undefined ? nodeRun.get(nodeId) : undefined;
        if (target) handleTaskCompleted(target, e);
        if (nodeId === pid) {
          participantStatuses.set(pid, participantStatusOf((e.data.status as ProgressNode['status'] | undefined) ?? 'completed'));
          participantMediaApprovals.delete(pid);
        }
        return;
      }
      case 'task_failed':
      case 'step_failed':
      case 'reasoning_failed': {
        const id = e.event_type === 'reasoning_failed' ? pid : nodeId;
        const target = id !== undefined ? nodeRun.get(id) : undefined;
        if (target) handleTaskFailed(target, { ...e, data: { ...e.data, id } });
        if (id === pid) {
          participantStatuses.set(pid, 'failed');
          participantMediaApprovals.delete(pid);
        }
        return;
      }
      case 'execution_stopped': {
        const target = nodeRun.get(pid);
        participantStatuses.set(pid, 'stopped');
        participantMediaApprovals.delete(pid);
        if (target) {
          for (const id of stopSubtree(target, pid, e.created_at)) {
            participantStatuses.set(id, 'stopped');
            participantMediaApprovals.delete(id);
          }
        }
        return;
      }
      // (sessions ADR-34ae2)
      case 'participant_status': {
        const status = e.data.status;
        if (status !== 'running' && status !== 'idle' && status !== 'settled' && status !== 'failed' && status !== 'stopped') {
          throw new Error(`participant_status event ${e.sequence_num} carries an unknown status ${String(status)}`);
        }
        participantStatuses.set(pid, status);
        const own = nodeRun.get(pid)?.nodes.find((n) => n.id === pid);
        if (own) applyParticipantStatus(own, status, e.created_at);
        if (status !== 'running' && status !== 'idle') participantMediaApprovals.delete(pid);
        return;
      }
      case 'media_action_approval_request': {
        const request = readMediaActionApproval(e);
        if (request) participantMediaApprovals.set(pid, request);
        return;
      }
      case 'media_action_approval_response':
        participantMediaApprovals.delete(pid);
        return;
      case 'user_message':
      case 'assistant_message':
        return;
      case 'reasoning_started':
      case 'reasoning_completed':
      case 'execution_redirected':
      case 'state_initialized':
      case 'process_selected':
      case 'queue_updated':
        return;
      case 'question_asked':
      case 'question_resolved':
        foldQuestion(questions, e);
        return;
      case 'tool_call_result': {
        const target = nodeId !== undefined ? nodeRun.get(nodeId) : undefined;
        if (target) appendStepToRun(target, e);
        const signal = readPlatformApproval(e, subagentPlatformStarts.get(pid) ?? null);
        if (signal.kind === 'pending') pendingPlatformApproval = signal.request;
        else if (signal.kind === 'cleared') pendingPlatformApproval = null;
        return;
      }
      default: {
        const target = nodeId !== undefined ? nodeRun.get(nodeId) : undefined;
        if (target) appendStepToRun(target, e);
        if (e.event_type === 'tool_call_start' && e.data.tool_name === 'platform_call') subagentPlatformStarts.set(pid, readPlatformCallStart(e));
      }
    }
  }

  for (const e of events) {
    if (isSubagentEvent(e)) {
      foldSubagentEvent(e);
      continue;
    }
    const owner = runForEvent(runsById, currentRun, e);
    if (owner) captureInternalAgent(owner, e);
    switch (e.event_type) {
      case 'reasoning_started':
        currentRun = {
          runIndex: runs.length,
          runId: requireRunId(e),
          runOrdinal: requireRunOrdinal(e),
          startedAt: e.created_at,
          endedAt: null,
          status: 'in_progress',
          steps: [],
          nodes: [],
          internalAgents: [],
          contextUsagePercent: 0,
        };
        if (e.data.workflow_id) {
          currentRun.workflowId = e.data.workflow_id as string;
          workflowId = e.data.workflow_id as string;
        }
        currentRun.selectedProcess = selectedProcessFrom(e.data);
        // (agents ADR-08705)
        if (e.data.process_selection === 'automatic') {
          currentRun.selectingProcessSince = e.created_at;
        }
        applyTurnTrigger(currentRun, e);
        captureInternalAgent(currentRun, e);
        runs.push(currentRun);
        runsById.set(currentRun.runId, currentRun);
        pendingMediaActionApproval = null;
        primaryWaitedIds = null;
        lastStopReason = null;
        break;

      case 'process_selected': {
        const target = runForEvent(runsById, currentRun, e);
        if (target) {
          target.selectingProcessSince = undefined;
          const selection = selectedProcessFrom(e.data);
          if (selection) target.selectedProcess = selection;
        }
        break;
      }

      case 'state_initialized':
        if (currentRun) {
          currentRun.agentConfig = e.data.agent_config as Record<string, unknown> | undefined;
          currentRun.toolNames = e.data.tool_names as string[] | undefined;
        }
        break;

      case 'reasoning_completed': {
        const target = runForEvent(runsById, currentRun, e);
        if (target) {
          target.status = 'completed';
          target.endedAt = e.created_at;
          if (target === currentRun) currentRun = null;
        }
        lastStopReason = null;
        break;
      }

      case 'reasoning_failed': {
        // (agents ADR-96511)
        const refusal =
          typeof e.data.workspace_refusal === 'string' ? e.data.workspace_refusal : undefined;
        const refusalMessage =
          typeof e.data.workspace_refusal_message === 'string'
            ? e.data.workspace_refusal_message
            : undefined;
        const err = refusalMessage ?? (e.data.error as string) ?? 'Reasoning failed';
        const target = runForEvent(runsById, currentRun, e);
        if (target) {
          target.status = 'failed';
          target.endedAt = e.created_at;
          target.error = err;
          target.workspaceRefusal = refusal;
          if (target === currentRun) currentRun = null;
        } else {
          // No run to attribute to — a pre-run gate failed before it started.
          // Synthesize one so the failure is visible, and register it under its
          // run_id so any later keyed event still finds it (agents ADR-043).
          const failed: InternalRun = {
            runIndex: runs.length,
            runId: requireRunId(e),
            runOrdinal: null,
            startedAt: e.created_at,
            endedAt: e.created_at,
            status: 'failed',
            error: err,
            workspaceRefusal: refusal,
            steps: [],
            nodes: [],
            internalAgents: [],
            contextUsagePercent: 0,
          };
          runs.push(failed);
          runsById.set(failed.runId, failed);
          currentRun = null;
        }
        lastStopReason = null;
        break;
      }

      case 'execution_stopped': {
        const target = runForEvent(runsById, currentRun, e);
        if (target) {
          target.status = 'stopped';
          target.endedAt = e.created_at;
          // A stopped run's nodes are stopped too. Leaving them 'running' left
          // permanent spinners inside a run the user had already ended.
          for (const node of target.nodes) {
            if (node.status === 'running') {
              node.status = 'stopped';
              node.endedAt = e.created_at;
            }
          }
          if (target === currentRun) currentRun = null;
        }
        lastStopReason = 'user_stop';
        break;
      }

      case 'execution_redirected':
        if (currentRun) {
          currentRun.status = 'completed';
          currentRun.endedAt = e.created_at;
          currentRun = null;
        }
        lastStopReason = 'user_redirect';
        break;

      case 'user_message':
        messages.push({
          id: e.id,
          role: 'user',
          content: (e.data.content as string) ?? '',
          timestamp: e.created_at,
          metadata: e.data.metadata as Record<string, unknown> | undefined,
        });
        break;

      case 'assistant_message': {
        messages.push({
          id: e.id,
          role: 'assistant',
          content: (e.data.content as string) ?? '',
          timestamp: e.created_at,
          metadata: e.data.metadata as Record<string, unknown> | undefined,
        });
        // Final replies usually emit before reasoning_completed, while the run
        // is still in_progress. Prefer the active run; only fall back to the
        // latest completed run without an id (late/out-of-order frames).
        const messageId = (e.data.message_id as string) ?? e.id;
        if (currentRun && !currentRun.assistantMessageId) {
          currentRun.assistantMessageId = messageId;
        } else {
          const closedRun = [...runs].reverse().find(
            (r) => r.status !== 'in_progress' && !r.assistantMessageId,
          );
          if (closedRun) {
            closedRun.assistantMessageId = messageId;
          }
        }
        break;
      }

      case 'question_asked':
      case 'question_resolved':
        foldQuestion(questions, e);
        break;

      case 'media_action_approval_request': {
        const request = readMediaActionApproval(e);
        if (request) pendingMediaActionApproval = request;
        break;
      }

      case 'media_action_approval_response':
        pendingMediaActionApproval = null;
        break;

      case 'task_started':
      case 'step_started': {
        const parentId = typeof e.data.parent_id === 'string' ? e.data.parent_id : undefined;
        const target = (parentId !== undefined ? nodeRun.get(parentId) : undefined) ?? currentRun;
        if (target) {
          const node = handleTaskStarted(target, e);
          if (node) nodeRun.set(node.id, target);
        }
        break;
      }

      case 'task_completed':
      case 'step_completed': {
        const target = nodeRun.get(e.data.id as string) ?? currentRun;
        if (target) handleTaskCompleted(target, e);
        break;
      }

      case 'task_failed':
      case 'step_failed': {
        const target = nodeRun.get(e.data.id as string) ?? currentRun;
        if (target) handleTaskFailed(target, e);
        break;
      }

      case 'tool_call_start': {
        if (currentRun) appendStepToRun(currentRun, e);
        if (e.data.tool_name === 'platform_call') lastPlatformCallStart = readPlatformCallStart(e);
        if (e.data.tool_name === 'wait_for_answers') primaryWaitedIds = readWaitedQuestionIds(e);
        break;
      }

      case 'tool_call_result': {
        if (currentRun) appendStepToRun(currentRun, e);
        if (e.data.tool_name === 'wait_for_answers') primaryWaitedIds = null;
        const signal = readPlatformApproval(e, lastPlatformCallStart);
        if (signal.kind === 'pending') {
          pendingPlatformApproval = signal.request;
        } else if (signal.kind === 'cleared') {
          pendingPlatformApproval = null;
        }
        break;
      }

      // (sessions ADR-d28b1)
      case 'queue_updated':
      case 'participant_status':
        break;
      default:
        if (owner) appendStepToRun(owner, e);
        break;
    }
  }

  // (context ADR-31fe6)
  for (let moved = true; moved; ) {
    moved = false;
    for (const node of [...detached.nodes]) {
      const home = node.parentId !== undefined ? nodeRun.get(node.parentId) : undefined;
      if (home === undefined || home === detached) continue;
      detached.nodes = detached.nodes.filter((n) => n.id !== node.id);
      home.nodes.push(node);
      nodeRun.set(node.id, home);
      moved = true;
    }
  }

  const lastLoaded = runs[runs.length - 1] as InternalRun | undefined;
  const detachedById = new Map(detached.nodes.map((n) => [n.id, n]));
  const isLive = (n: ProgressNode): boolean =>
    n.status === 'running' || n.participantStatus === 'running' || n.participantStatus === 'idle';
  const carried = new Set<string>();
  for (const node of detached.nodes) {
    for (let at: ProgressNode | undefined = node; at !== undefined; at = at.parentId !== undefined ? detachedById.get(at.parentId) : undefined) {
      if (isLive(at)) {
        carried.add(node.id);
        break;
      }
    }
  }
  if (lastLoaded) {
    for (const node of detached.nodes) {
      if (!carried.has(node.id)) continue;
      node.carriedOver = true;
      if (!node.startedAt) node.startedAt = lastLoaded.startedAt;
      lastLoaded.nodes.push(node);
    }
  }
  buildNodeTree(detached.nodes);

  for (const run of runs) {
    buildNodeTree(run.nodes);
    rerouteOrphanedSteps(run);
  }

  // The session is reasoning while ANY run is open, not merely the last one.
  // Reading only the last run reported 'idle' with a workflow still executing,
  // which is what let a second send slip past the queue gate (agents ADR-043).
  const lastRun = runs[runs.length - 1] as InternalRun | undefined;
  const anyOpen = runs.some((r) => r.status === 'in_progress');
  let state: ChatState = 'idle';
  if (anyOpen) {
    state = 'reasoning';
  } else if (lastRun) {
    if (lastRun.status === 'failed') {
      state = 'error';
    } else if (lastRun.status === 'stopped' || lastStopReason === 'user_stop') {
      state = 'stopped';
    }
  }

  const reasoningRuns = runs.map(runToProgressTree);

  // (agents ADR-14bdc)
  const waitingOnUser =
    anyOpen &&
    primaryWaitedIds !== null &&
    primaryWaitedIds.some((id) => {
      const q = questions.get(id);
      return q !== undefined && q.status === 'open' && q.addresseeKind === 'user';
    });

  const orderedQuestions = new Map(
    [...questions.entries()].sort(([, a], [, b]) => Date.parse(a.askedAt) - Date.parse(b.askedAt)),
  );

  const progressTree = lastRun ? runToProgressTree(lastRun) : null;

  const participantNodes = new Map<string, ProgressNode>();
  const participantOwners = new Map<string, string | null>();
  for (const id of subagentIds) {
    const run = nodeRun.get(id);
    const node = run?.nodes.find((n) => n.id === id);
    if (!run || !node) continue;
    participantNodes.set(id, node);
    const spawn = node.parentId !== undefined ? run.nodes.find((n) => n.id === node.parentId) : undefined;
    participantOwners.set(id, spawn?.participantId ?? null);
  }
  for (const p of options.participants ?? []) {
    const node = detachedById.get(p.id);
    if (!node) continue;
    participantNodes.set(p.id, node);
    participantOwners.set(p.id, p.ownerParticipantId);
  }

  const liveBackgroundOwners = new Set<string>();
  const loadedNodes = new Map<string, ProgressNode>();
  for (const node of [...runs.flatMap((run) => run.nodes), ...detached.nodes]) {
    loadedNodes.set(node.id, node);
    if (node.background !== true || node.status !== 'running' || isParticipantNode(node)) continue;
    if (node.participantId !== undefined) liveBackgroundOwners.add(node.participantId);
  }
  for (const task of options.backgroundTasks ?? []) {
    const node = loadedNodes.get(task.id);
    if (node === undefined || node.status === 'running') liveBackgroundOwners.add(task.owner_participant_id);
  }

  return {
    messages,
    state,
    progressTree,
    questions: orderedQuestions,
    waitingOnUser,
    pendingPlatformApproval,
    pendingMediaActionApproval,
    workflowId,
    reasoningRuns,
    lastStopReason,
    primaryParticipantId,
    participantNodes,
    participantOwners,
    participantStatuses,
    liveBackgroundOwners,
    participantMediaApprovals,
    detachedNodes: detachedById,
  };
}
